import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Label, HelperText, ErrorText } from './form-helpers.js';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helperText, errorText, leftAdornment, rightAdornment, className, id: idProp, disabled, type = 'text', ...rest },
  ref,
) {
  const generated = useId();
  const id = idProp ?? generated;
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div
        className={cn(
          'relative flex items-stretch rounded-md bg-bg-surface-2 border border-border-strong transition-colors',
          'focus-within:border-accent-base focus-within:ring-2 focus-within:ring-accent-base/30',
          errorText && 'border-danger focus-within:border-danger focus-within:ring-danger/30',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        {leftAdornment && (
          <span className="flex items-center pl-3 text-text-secondary">{leftAdornment}</span>
        )}
        <input
          ref={ref}
          id={id}
          type={type}
          disabled={disabled}
          aria-invalid={!!errorText || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-12 flex-1 bg-transparent px-3.5 text-base text-text-primary placeholder:text-text-tertiary outline-none',
            leftAdornment && 'pl-2',
            rightAdornment && 'pr-2',
            className,
          )}
          {...rest}
        />
        {rightAdornment && (
          <span className="flex items-center pr-3 text-text-secondary">{rightAdornment}</span>
        )}
      </div>
      {errorText && <ErrorText id={errorId}>{errorText}</ErrorText>}
      {helperText && !errorText && <HelperText className="" >{helperText}</HelperText>}
    </div>
  );
});
